import { RefObject, useEffect } from "react";
import { postIpc } from "./ipc";

type LayoutSelection = {
  projectId?: string;
  canvasId?: string;
  slabId?: string;
  blockId?: string;
};

type LayoutRoute = {
  id: string;
  title: string;
  kind: string;
  canvas_id?: string;
  built_in_route?: string;
  draft?: boolean;
  valid?: boolean;
  fallback_route_id?: string;
};

type LayoutEdge = {
  id: string;
  from_route_id: string;
  to_route_id: string;
  label: string;
  kind: string;
  visible?: boolean;
};

type UseLayoutSurfaceReportOptions = {
  route: string;
  surfaceId: string;
  rootRef: RefObject<HTMLElement | null>;
  selection?: LayoutSelection;
  routes?: LayoutRoute[];
  edges?: LayoutEdge[];
  deps?: Array<string | number | boolean | null | undefined>;
};

function toNumber(value?: string) {
  const parsed = Number(value ?? "");
  return Number.isFinite(parsed) ? parsed : 0;
}

export function useLayoutSurfaceReport({
  route,
  surfaceId,
  rootRef,
  selection,
  routes = [],
  edges = [],
  deps = [],
}: UseLayoutSurfaceReportOptions) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let raf = 0;
    const report = () => {
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
        page_scroll_x: document.documentElement.scrollWidth > window.innerWidth + 1,
        page_scroll_y: document.documentElement.scrollHeight > window.innerHeight + 1,
      };

      const nodes = Array.from(root.querySelectorAll<HTMLElement>("[data-layout-node]")).map((node) => {
        const rect = node.getBoundingClientRect();
        return {
          id: node.dataset.layoutId || node.dataset.layoutNode || "",
          kind: node.dataset.layoutKind || "block",
          title: node.dataset.layoutTitle || "",
          parent_id: node.dataset.layoutParent || "",
          grid: {
            column: toNumber(node.dataset.gridColumn),
            row: toNumber(node.dataset.gridRow),
            width: toNumber(node.dataset.gridWidth),
            height: toNumber(node.dataset.gridHeight),
          },
          pixels: {
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: rect.height,
          },
          overflow: {
            overflow_x: node.scrollWidth > node.clientWidth + 1,
            overflow_y: node.scrollHeight > node.clientHeight + 1,
            clipped: node.scrollWidth > node.clientWidth + 1 || node.scrollHeight > node.clientHeight + 1,
            fits_container: node.scrollWidth <= node.clientWidth + 1 && node.scrollHeight <= node.clientHeight + 1,
            scroll_width: node.scrollWidth,
            scroll_height: node.scrollHeight,
            client_width: node.clientWidth,
            client_height: node.clientHeight,
          },
          metadata: {
            route,
          },
          capabilities: {
            movable: node.dataset.capMove === "true",
            resizable: node.dataset.capResize === "true",
            wrappable: node.dataset.capWrap === "true",
            remixable: node.dataset.capRemix === "true",
            stageable: node.dataset.capStage === "true",
            savable: node.dataset.capSave === "true",
            internal_scroll: node.dataset.capInternalScroll === "true",
          },
        };
      });

      const violations = [];
      if (viewport.page_scroll_x || viewport.page_scroll_y) {
        violations.push({
          node_id: surfaceId,
          kind: "page_scroll",
          message: "page scroll detected on surface",
        });
      }

      const payload = {
        route,
        surface_id: surfaceId,
        viewport,
        nodes,
        violations,
        timestamp: new Date().toISOString(),
        selection: {
          project_id: selection?.projectId ?? "",
          canvas_id: selection?.canvasId ?? "",
          slab_id: selection?.slabId ?? "",
          block_id: selection?.blockId ?? "",
        },
        navigation: {
          home_route_id: routes[0]?.id ?? "",
          routes,
          edges,
        },
      };

      postIpc({ kind: "layout-surface-report", data: JSON.stringify(payload) });
    };

    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(report);
    };

    const observer = new ResizeObserver(schedule);
    observer.observe(root);
    root.querySelectorAll<HTMLElement>("[data-layout-node]").forEach((node) => observer.observe(node));
    window.addEventListener("resize", schedule);
    schedule();

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [route, surfaceId, rootRef, selection?.projectId, selection?.canvasId, selection?.slabId, selection?.blockId, ...deps]);
}
